// UI metadata for the master-product unit-of-measure fields. DB/API names stay
// unchanged (baseUnit/itemUnit/netContent/packUnit/packSize); only labels differ.
// The per-field option lists that used to live here were merged into one vocabulary
// that the backend now serves, fetched via useGetUomOptionsApi — every UOM dropdown
// offers the same words and the operator picks the correct rung. The fields keep their distinct
// meanings; only the restriction on which words may fill them is gone.
// See docs/superpowers/specs/2026-07-29-uom-consolidation-design.md.

import { Box, HStack, InfoIcon, Text, Tooltip } from '@elkaso-app/web-design';

export const UOM_HELP = {
  baseUnit: {
    label: 'Fundamental Unit',
    hint: 'The unit an item is measured with — used to compare prices across different variants, e.g. KG or L.',
  },
  itemUnit: {
    label: 'Base Unit',
    hint: 'The unit an item is divided into, e.g. Chicken whole has 10 pieces in a carton.',
  },
  netContent: {
    label: 'Base quantity',
    hint: 'How much of the Fundamental Unit is in each Base Unit, e.g. Chicken griller 700 grams is 0.7 KG.',
  },
  packUnit: {
    label: 'Packaging Unit',
    hint: 'How the package is named, e.g. Carton, Box, Case.',
  },
  packSize: {
    label: 'Package quantity',
    hint: 'How many Base Units are in each package, e.g. 10 pieces per carton.',
  },
} as const;

interface LabelWithHintProps {
  label: string;
  hint: string;
}

// Inline label + info tooltip, usable inside FormLabel and table Th alike.
export const LabelWithHint = ({ label, hint }: LabelWithHintProps) => (
  <HStack as='span' display='inline-flex' spacing='1' align='center'>
    <Text as='span'>{label}</Text>
    <Tooltip label={hint} hasArrow placement='top'>
      <Box as='span' display='inline-flex' color='gray.400' cursor='help'>
        <InfoIcon />
      </Box>
    </Tooltip>
  </HStack>
);
